import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { type AdminProduct, type CatalogAdminView, useCatalogMutation } from "../../hooks/catalog";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName, money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { MoneyInput, Switch, IntInput } from "./fields";

const KINDS = ["play_time", "addon", "fnb", "merch", "party_package", "party_addon", "school_package"] as const;
type Kind = (typeof KINDS)[number];
const SELLABLE: readonly string[] = [...KINDS];

interface Form {
  kind: Kind; sku: string; barcode: string; name: string; nameAr: string; price: number | null; taxRateId: string | null; active: boolean;
  trackStock: boolean; initialQty: number; parLevel: number; branchActive: boolean; priceOverride: number | null;
}

function ProductDialog({ product, data, onClose }: { product: AdminProduct | null; data: CatalogAdminView; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const [f, setF] = useState<Form>(
    product
      ? { kind: product.kind as Kind, sku: product.sku, barcode: product.barcode ?? "", name: product.name, nameAr: product.nameAr ?? "", price: product.price, taxRateId: product.taxRateId, active: product.active, trackStock: product.consumesStock, initialQty: 0, parLevel: 0, branchActive: product.branchActive, priceOverride: product.priceOverride }
      : { kind: "fnb", sku: "", barcode: "", name: "", nameAr: "", price: 0, taxRateId: data.taxRates[0]?.id ?? null, active: true, trackStock: true, initialQty: 0, parLevel: 0, branchActive: true, priceOverride: null },
  );
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const isTicket = f.kind === "play_time";
  // the recipe: what selling one takes off the shelf (loaded for existing products, empty for a new one)
  type Part = { stockItemId: string; qty: number; wastePct: number; optional: boolean };
  const [recipe, setRecipe] = useState<Part[] | null>(product ? null : []);
  const [recipeEdited, setRecipeEdited] = useState(false);
  useEffect(() => {
    if (!product || isTicket) return;
    void api.get<{ items: Array<{ stock_item_id: string; qty: number; waste_pct: number; optional: boolean }> }>(`/catalog-admin/products/${product.id}/recipe`).then((r) => setRecipe(r.items.map((i) => ({ stockItemId: i.stock_item_id, qty: i.qty, wastePct: i.waste_pct, optional: i.optional }))));
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setPart = (i: number, patch: Partial<Part>) => { setRecipe((r) => (r ?? []).map((p, k) => (k === i ? { ...p, ...patch } : p))); setRecipeEdited(true); };
  const save = useCatalogMutation(async () => {
    if (!product) {
      return api.post("/catalog-admin/products", {
        sku: f.sku.trim(), barcode: f.barcode.trim() || null, name: f.name.trim(), nameAr: f.nameAr.trim() || null, kind: f.kind, price: isTicket ? null : (f.price ?? 0), taxRateId: f.taxRateId,
        trackStock: !isTicket && f.trackStock, initialQty: f.initialQty, parLevel: f.parLevel, active: f.active,
      });
    }
    await api.patch(`/catalog-admin/products/${product.id}`, {
      sku: f.sku.trim(), barcode: f.barcode.trim() || null, name: f.name.trim(), nameAr: f.nameAr.trim() || null, taxRateId: f.taxRateId, active: f.active, ...(isTicket ? {} : { price: f.price ?? 0 }),
    });
    if (recipeEdited && recipe) await api.put(`/catalog-admin/products/${product.id}/recipe`, { items: recipe });
    if (f.branchActive !== product.branchActive || f.priceOverride !== product.priceOverride) await api.put(`/catalog-admin/products/${product.id}/branch`, { active: f.branchActive, priceOverride: isTicket ? null : f.priceOverride });
    return null;
  });
  const valid = f.sku.trim().length > 0 && f.name.trim().length > 0 && (isTicket || f.price !== null);
  async function submit() {
    try {
      await save.mutateAsync(undefined);
      toast(t("pricing.saved"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    }
  }
  const cur = t("currency");
  return (
    <Modal open size="md" onClose={onClose} title={product ? t("pricing.product.editTitle") : t("pricing.product.newTitle")}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!valid || save.isPending} onClick={() => void submit()}>{t("pricing.save")}</button></>}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="pd-kind">{t("pricing.product.kind")}</label>
          <select id="pd-kind" className="field" value={f.kind} disabled={!!product} onChange={(e) => set("kind", e.target.value as Kind)}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`pricing.kind.${k}`)}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="pd-sku">{t("pricing.product.sku")}</label>
          <input id="pd-sku" className="field num font-mono" value={f.sku} maxLength={40} onChange={(e) => set("sku", e.target.value.toUpperCase())} />
        </div>
        <div>
          <label className="label" htmlFor="pd-name">{t("pricing.product.name")}</label>
          <input id="pd-name" className="field" value={f.name} maxLength={120} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="pd-name-ar">{t("pricing.product.nameAr")}</label>
          <input id="pd-name-ar" className="field" dir="rtl" value={f.nameAr} maxLength={120} onChange={(e) => set("nameAr", e.target.value)} />
        </div>
        {!isTicket && (
          <div>
            <label className="label" htmlFor="pd-price">{t("pricing.product.price", { cur })}</label>
            <MoneyInput id="pd-price" value={f.price} onChange={(v) => set("price", v)} />
          </div>
        )}
        <div>
          <label className="label" htmlFor="pd-tax">{t("pricing.product.tax")}</label>
          <select id="pd-tax" className="field" value={f.taxRateId ?? ""} onChange={(e) => set("taxRateId", e.target.value || null)}>
            <option value="">{t("pricing.product.branchTax")}</option>
            {data.taxRates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.rate_bp / 100}%)</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="pd-barcode">{t("pricing.product.barcode")}</label>
          <input id="pd-barcode" className="field num font-mono" value={f.barcode} maxLength={64} onChange={(e) => set("barcode", e.target.value)} />
        </div>
      </div>
      {isTicket && <p className="rounded-xl bg-purple-50 p-2.5 text-[11px] font-bold text-cosmic-purple">ℹ️ {t("pricing.product.ticketHelp")}</p>}
      {!product && !isTicket && (
        <fieldset className="space-y-2 rounded-xl border border-gray-200 p-3">
          <Switch checked={f.trackStock} onChange={(v) => set("trackStock", v)}>{t("pricing.product.trackStock")}</Switch>
          {f.trackStock && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label" htmlFor="pd-qty">{t("pricing.product.openingQty")}</label><IntInput id="pd-qty" value={f.initialQty} onChange={(n) => set("initialQty", n)} /></div>
              <div><label className="label" htmlFor="pd-par">{t("pricing.product.par")}</label><IntInput id="pd-par" value={f.parLevel} onChange={(n) => set("parLevel", n)} /></div>
            </div>
          )}
        </fieldset>
      )}
      {product && (
        <fieldset className="space-y-2 rounded-xl border border-gray-200 p-3">
          <legend className="px-1 text-[11px] font-black text-gray-600">{t("pricing.product.thisBranch")}</legend>
          <Switch checked={f.branchActive} onChange={(v) => set("branchActive", v)}>{t("pricing.product.soldHere")}</Switch>
          {!isTicket && (
            <div className="max-w-48">
              <label className="label" htmlFor="pd-override">{t("pricing.product.branchPrice", { cur })}</label>
              <MoneyInput id="pd-override" value={f.priceOverride} allowEmpty placeholder={t("pricing.product.sameAsCompany")} onChange={(v) => set("priceOverride", v)} />
            </div>
          )}
        </fieldset>
      )}
      {product && !isTicket && recipe && (
        <fieldset className="space-y-2 rounded-xl border border-gray-200 p-3" data-testid="recipe">
          <legend className="px-1 text-[11px] font-black text-gray-600">{t("pricing.recipe.title")}</legend>
          <p className="text-[11px] font-semibold text-gray-500">{t("pricing.recipe.help")}</p>
          {recipe.map((p, i) => (
            <div key={i} className="grid grid-cols-12 items-end gap-2" data-part={i + 1}>
              <div className="col-span-6"><label className="label" htmlFor={`rc-item-${i}`}>{t("pricing.recipe.item")}</label>
                <select id={`rc-item-${i}`} className="field" value={p.stockItemId} onChange={(e) => setPart(i, { stockItemId: e.target.value })}>{data.stock.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name} ({t(`pricing.stock.uoms.${s.uom}`)})</option>)}</select></div>
              <div className="col-span-3"><label className="label" htmlFor={`rc-qty-${i}`}>{t("pricing.recipe.qty")}</label>
                <input id={`rc-qty-${i}`} className="field num font-mono" inputMode="decimal" defaultValue={p.qty} onChange={(e) => { const n = Number(e.target.value); if (n > 0) setPart(i, { qty: n }); }} /></div>
              <div className="col-span-2"><label className="label" htmlFor={`rc-waste-${i}`}>{t("pricing.recipe.waste")}</label><IntInput id={`rc-waste-${i}`} value={p.wastePct} max={100} onChange={(n) => setPart(i, { wastePct: n })} /></div>
              <button type="button" className="btn-ghost col-span-1 !px-2 text-red-600" aria-label={t("pricing.recipe.remove", { n: i + 1 })} onClick={() => { setRecipe(recipe.filter((_, k) => k !== i)); setRecipeEdited(true); }}>🗑</button>
            </div>
          ))}
          <button type="button" className="btn-ghost text-xs" disabled={data.stock.length === 0} onClick={() => { const free = data.stock.find((s) => s.active && !recipe.some((p) => p.stockItemId === s.id)) ?? data.stock[0]; if (free) { setRecipe([...recipe, { stockItemId: free.id, qty: 1, wastePct: 0, optional: false }]); setRecipeEdited(true); } }}>＋ {t("pricing.recipe.add")}</button>
        </fieldset>
      )}
      <Switch checked={f.active} onChange={(v) => set("active", v)}>{t("pricing.product.active")}</Switch>
    </Modal>
  );
}

export function ProductsPanel({ data, canEdit }: { data: CatalogAdminView; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [filter, setFilter] = useState<"all" | Kind>("all");
  const [dialog, setDialog] = useState<{ product: AdminProduct | null } | null>(null);
  const list = data.products.filter((p) => SELLABLE.includes(p.kind) && (filter === "all" || p.kind === filter));
  const cur = t("currency");
  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("pricing.tabs.products")}>
      <SectionHeader
        title={`🛍️ ${t("pricing.tabs.products")}`}
        subtitle={t("pricing.product.subtitle")}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 text-[10px] font-bold" role="group" aria-label={t("pricing.product.kind")}>
              {(["all", ...KINDS] as const).map((k) => (
                <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)} className={`rounded-lg px-2 py-1 ${filter === k ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-700"}`}>{k === "all" ? t("pricing.product.all") : t(`pricing.kind.${k}`)}</button>
              ))}
            </div>
            {canEdit && <button type="button" className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setDialog({ product: null })}>＋ {t("pricing.product.add")}</button>}
          </div>
        }
      />
      {list.length === 0 ? (
        <Empty>{t("pricing.product.none")}</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-start text-xs">
            <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("pricing.product.name")}</th>
                <th className="px-2 py-2 text-start">{t("pricing.product.kind")}</th>
                <th className="px-2 py-2 text-end">{t("pricing.product.priceShort")}</th>
                <th className="px-2 py-2 text-end">{t("pricing.product.stock")}</th>
                <th className="px-2 py-2 text-center">{t("pricing.product.status")}</th>
                <th className="px-3 py-2 text-end" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
              {list.map((p) => (
                <tr key={p.id} className={p.active && p.branchActive ? "" : "opacity-60"} data-sku={p.sku}>
                  <td className="px-3 py-2.5"><div className="font-extrabold text-cosmic-navy">{localName(p, locale)}</div><div className="num font-mono text-[10px] text-gray-400">{p.sku}</div></td>
                  <td className="px-2 py-2.5">{t(`pricing.kind.${p.kind}`)}</td>
                  <td className="num px-2 py-2.5 text-end font-mono">
                    {p.kind === "play_time" ? <span className="text-gray-400">{t("pricing.product.byRule")}</span> : (
                      <>{money(p.priceOverride ?? p.price ?? 0)} {cur}{p.priceOverride !== null && <span className="ms-1 text-[9px] text-amber-700">({t("pricing.product.branchTag")})</span>}</>
                    )}
                  </td>
                  <td className="num px-2 py-2.5 text-end">{p.consumesStock ? (p.qtyOnHand ?? 0) : "—"}</td>
                  <td className="px-2 py-2.5 text-center">{p.active && p.branchActive ? <Pill tone="green">{t("pricing.product.on")}</Pill> : <Pill tone="gray">{t("pricing.product.off")}</Pill>}</td>
                  <td className="px-3 py-2.5 text-end">{canEdit && <button type="button" className="btn-ghost !px-2.5 !py-1 text-xs" onClick={() => setDialog({ product: p })}>✏️ {t("pricing.edit")}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog && <ProductDialog product={dialog.product} data={data} onClose={() => setDialog(null)} />}
    </section>
  );
}
